// ============================================================================
// CLEO'S SECRET — TierCard
// File: src/components/marketing/TierCard.jsx
//
// Card primitive shared by Membership tiers (Knowledge / Sovereignty / Ritual)
// and Program tiers (Initiation / Realignment / Mastery) on MembershipPage.
// The middle item in each set carries `highlighted` to render a thin GO2
// border + small-caps mini-eyebrow above the card. `highlightLabel` lets the
// programs row read "MOST RECOMMENDED" while memberships read "MOST CHOSEN".
//
// Non-highlighted cards include a 30px top spacer so all cards begin at the
// same y-coordinate inside their grid column. The grid uses align-items:
// stretch; the CTA inside each card is pushed to the bottom via
// `margin-top: auto`.
//
// Props:
//   name           string   tier or program name
//   price          string   primary numeral including currency ("$299" / "$3,500")
//   cadence        string   secondary label ("per month" / "upfront")
//   essence        string   italic one-line clinical essence
//   body           string   optional paragraph between essence and inclusions
//                           (program cards only — memberships pass nothing)
//   inclusions     string[] lines rendered as 9px diamond-bulleted list
//   bundled        string   optional italic gold line between inclusions and
//                           CTA (program cards only — bundled-membership value)
//   ctaLabel       string   button copy
//   onCtaClick     fn       click handler
//   highlighted    boolean  middle-card emphasis — GO2 border + small-caps label
//   highlightLabel string   text for the small-caps label (default "Most chosen")
//   hairlineWidth  number   width in px of the two flanking hairlines for the
//                           highlight label (default 24; pass 16 for longer
//                           labels at narrow viewports)
// ============================================================================

import React from 'react';
import { CINZEL, P, T, BtnPrimary } from '../../lib/design-tokens';

export default function TierCard({
  name,
  price,
  cadence,
  essence,
  body,
  inclusions,
  bundled,
  ctaLabel,
  onCtaClick,
  highlighted,
  highlightLabel,
  hairlineWidth,
}) {
  var cadenceLabel = cadence || 'per month';
  var isHighlighted = highlighted === true;
  var labelText = highlightLabel || 'Most chosen';
  var hairline = typeof hairlineWidth === 'number' ? hairlineWidth : 24;
  var borderColor = isHighlighted ? P.GO2 : P.SAND3;
  var borderWidth = isHighlighted ? 1.5 : 1;
  var boxShadow = isHighlighted ? '0 0 0 4px rgba(224, 198, 133, 0.06)' : 'none';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top zone — small-caps highlight label on highlighted card, matching
          spacer on others. Both occupy ~30px so all cards' tops align in the
          grid row. Hairline width is configurable so longer labels
          ("MOST RECOMMENDED") can shrink the flanking rules for narrow
          viewports. */}
      {isHighlighted ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          marginBottom: 12,
          height: 18,
        }}>
          <div style={{ width: hairline, height: 1, background: P.GO, opacity: 0.6 }} />
          <span style={{
            fontFamily: CINZEL,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.GO,
          }}>{labelText}</span>
          <div style={{ width: hairline, height: 1, background: P.GO, opacity: 0.6 }} />
        </div>
      ) : (
        <div style={{ height: 30 }} aria-hidden="true" />
      )}

      {/* Card */}
      <div style={{
        flex: 1,
        background: 'rgba(250, 246, 238, 0.55)',
        padding: 'clamp(40px, 4.5vw, 60px) clamp(28px, 3.2vw, 44px) clamp(36px, 4vw, 52px)',
        border: borderWidth + 'px solid ' + borderColor,
        boxShadow: boxShadow,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        {/* Tier name — Cinzel small caps, BR2 on cream */}
        <h3 style={{
          fontFamily: CINZEL,
          fontSize: 'clamp(22px, 2.4vw, 28px)',
          fontWeight: 600,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          margin: 0,
        }}>{name}</h3>

        {/* Hairline rule */}
        <div style={{
          width: 'clamp(40px, 4vw, 56px)',
          height: 1,
          background: P.GO,
          opacity: 0.65,
          marginTop: 22,
          marginBottom: 28,
        }} aria-hidden="true" />

        {/* Price + cadence stack */}
        <div style={{
          ...T.statNum,
          color: P.INK,
          marginBottom: 8,
        }}>{price}</div>
        <div style={{
          ...T.label,
          color: P.TF,
          marginBottom: 28,
        }}>{cadenceLabel}</div>

        {/* Essence — italic Cormorant 500, BR2 */}
        <p style={{
          ...T.bodyMdItalic,
          color: P.BR2,
          maxWidth: 280,
          marginBottom: body ? 20 : 32,
        }}>{essence}</p>

        {/* Optional body paragraph — programs only. Sits between essence and
            inclusions. Memberships pass no body and this block is skipped. */}
        {body ? (
          <p style={{
            ...T.bodyMd,
            color: P.TX,
            maxWidth: 320,
            marginBottom: 32,
            textAlign: 'left',
            hyphens: 'auto',
          }}>{body}</p>
        ) : null}

        {/* Inclusions — diamond-bulleted list */}
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: bundled ? '0 0 24px' : '0 0 36px',
          textAlign: 'left',
          width: '100%',
          maxWidth: 320,
        }}>
          {inclusions.map(function(item, i) {
            var isLast = i === inclusions.length - 1;
            return (
              <li key={i} style={{
                position: 'relative',
                paddingLeft: 26,
                marginBottom: isLast ? 0 : 16,
                ...T.bodyMd,
                color: P.TX,
              }}>
                <span style={{
                  position: 'absolute',
                  left: 6,
                  top: '0.55em',
                  width: 8,
                  height: 8,
                  background: P.BR,
                  transform: 'rotate(45deg)',
                }} aria-hidden="true" />
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </li>
            );
          })}
        </ul>

        {/* Optional bundled-value line — programs only. Italic gold, sits
            between inclusions and CTA. Memberships pass nothing. */}
        {bundled ? (
          <p style={{
            ...T.bodyMdItalic,
            color: P.BR2,
            maxWidth: 320,
            marginBottom: 28,
            textAlign: 'center',
          }}>{bundled}</p>
        ) : null}

        {/* CTA — pushed to bottom via margin-top: auto for equal-height grid alignment */}
        <div style={{ marginTop: 'auto', width: '100%' }}>
          <BtnPrimary onClick={onCtaClick}>{ctaLabel}</BtnPrimary>
        </div>
      </div>
    </div>
  );
}
